/**
 * Canonical, hreflang (incluido x-default) y Open Graph de páginas indexables.
 * `path` es la ruta tras el prefijo de idioma (`''` en la home, `'/hoteliers'`, `'/slug-id.html'`).
 */
export function usePublicSeo(options: {
  title: MaybeRefOrGetter<string | undefined>
  description: MaybeRefOrGetter<string | undefined>
  path: MaybeRefOrGetter<string>
  ogTitle?: MaybeRefOrGetter<string | undefined>
  ogDescription?: MaybeRefOrGetter<string | undefined>
  ogImage?: MaybeRefOrGetter<string | undefined>
  ogType?: 'website' | 'article'
}) {
  const { locale } = useI18n()
  const runtimeConfig = useRuntimeConfig()

  const baseUrl = computed(() => (runtimeConfig.public.BASE_URL || '').replace(/\/$/, ''))

  const normalizedPath = computed(() => {
    const raw = toValue(options.path) || ''
    if (!raw || raw === '/') return ''
    return raw.startsWith('/') ? raw : `/${raw}`
  })

  const canonicalUrl = computed(() => `${baseUrl.value}/${locale.value}${normalizedPath.value}`)

  const alternates = computed(() => {
    const links: { rel: 'alternate'; hreflang: string; href: string }[] = PUBLIC_LOCALES.map((code) => ({
      rel: 'alternate',
      hreflang: code,
      href: `${baseUrl.value}/${code}${normalizedPath.value}`,
    }))
    links.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${baseUrl.value}/${DEFAULT_LOCALE}${normalizedPath.value}`,
    })
    return links
  })

  useSeoMeta({
    title: () => toValue(options.title) ?? '',
    description: () => toValue(options.description) ?? '',
    ogTitle: () => toValue(options.ogTitle) ?? toValue(options.title) ?? '',
    ogDescription: () => toValue(options.ogDescription) ?? toValue(options.description) ?? '',
    ogUrl: canonicalUrl,
    ogType: options.ogType ?? 'website',
    ogLocale: () => {
      const code = locale.value
      if (isLocaleCode(code)) return OG_LOCALE_BY_CODE[code]
      return OG_LOCALE_BY_CODE[DEFAULT_LOCALE]
    },
    ...(options.ogImage
      ? {
          ogImage: () => toValue(options.ogImage) || DEFAULT_OG_IMAGE,
          twitterImage: () => toValue(options.ogImage) || DEFAULT_OG_IMAGE,
        }
      : {}),
  })

  useHead({
    link: computed(() => [
      { rel: 'canonical', href: canonicalUrl.value },
      ...alternates.value,
    ]),
  })

  return { canonicalUrl, baseUrl }
}
